// Practice-mode corpus loader. Loads the bundled hand-curated 10-puzzle set
// from `data/practice-puzzles.json`, validates the shape at module load, and
// exposes accessors that mirror the daily corpus's adapter pattern.
//
// Validation policy: if ANY validation fails (length !== 10, missing fields,
// wrong types), we log an error, set `practiceAvailable = false`, and leave
// the entries array empty. We DO NOT throw — the daily-puzzle path must remain
// available even if the practice corpus is corrupted. Callers check
// `isPracticeAvailable()` before offering the practice CTA.
//
// On-wire shape matches the daily corpus's `WireChoice` ({s, d}) so the same
// space-saving keys carry across both bundles. The translation to the runtime
// `Puzzle` shape happens here, mirroring `corpusEntryToPuzzle` in corpus.ts.
//
// TODO (deferred per implementation plan, owner: architect): once practice
// puzzles ship and bake in, consider removing them from the daily corpus so a
// player who's solved the practice set doesn't see the same puzzle resurface
// as today's daily ~years later. Tracked separately; out of scope for this
// pass.

import practiceJson from '../data/practice-puzzles.json'
import type { Puzzle } from './puzzle'
import {
  parseWireSolutionGraph,
  type SolutionGraph,
  type WireSolutionGraph,
} from './solutionGraph'

interface WirePracticePuzzle {
  readonly id: string
  readonly targetWord: string
  readonly wordLength: number
  readonly initialLayout: readonly number[]
  readonly letterByTokenIdx: readonly string[]
  // Path-dependent solution DAG (spec §10) — the sole authority for slot color.
  // Present on every entry in the enriched practice corpus; typed optional so a
  // malformed graph degrades gracefully. The graph is shared, shape-for-shape, with the daily
  // corpus; practice.ts imports the parser from solutionGraph.ts (a peer
  // module), NOT from corpus.ts, keeping the two corpora's validation
  // independent. isValidEntry below rejects a present-but-malformed graph rather
  // than silently dropping it, so a bad practice graph fails the whole entry
  // loud (corpus disabled) instead of degrading to the union path unnoticed.
  readonly solution_graph?: WireSolutionGraph
}

const EXPECTED_COUNT = 10

// Module-internal state. Populated at module load; never mutated afterward.
let practiceAvailable = false
const practiceEntries: WirePracticePuzzle[] = []

// Validate one entry. Returns true if the entry's shape matches the
// WirePracticePuzzle contract.
function isValidEntry(v: unknown): v is WirePracticePuzzle {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (typeof o['id'] !== 'string') return false
  if (typeof o['targetWord'] !== 'string') return false
  if (typeof o['wordLength'] !== 'number') return false
  if (!Array.isArray(o['initialLayout'])) return false
  for (const c of o['initialLayout']) {
    if (typeof c !== 'number') return false
  }
  if (!Array.isArray(o['letterByTokenIdx'])) return false
  for (const l of o['letterByTokenIdx']) {
    if (typeof l !== 'string') return false
  }
  // Optional solution_graph: absent is fine (older data); present must parse via
  // the shared validator to a non-undefined runtime graph. parseWireSolutionGraph
  // returns undefined on ANY malformation, so this rejects a partial/corrupt
  // graph and fails the whole entry — for the player-facing practice path we
  // prefer a loud whole-corpus-disabled failure over a silently inert graph.
  if (o['solution_graph'] !== undefined) {
    if (parseWireSolutionGraph(o['solution_graph']) === undefined) return false
  }
  return true
}

// One-shot load + validate at module init. Wrapped in try/catch to ensure any
// unexpected throw (e.g. JSON shape that confounds the type cast) still leaves
// the practice path disabled rather than crashing the whole bundle.
try {
  // The Vite typed-JSON-import pattern: the JSON shape is produced by a
  // hand-curated file, so we cast to `unknown` first and then validate at
  // runtime rather than trusting the cast (this is NOT an `as any` escape —
  // every field is checked by isValidEntry below).
  const raw = practiceJson as unknown
  if (!Array.isArray(raw)) {
    throw new Error(
      `practice-puzzles.json must be an array, got ${typeof raw}`,
    )
  }
  if (raw.length !== EXPECTED_COUNT) {
    throw new Error(
      `practice-puzzles.json must contain exactly ${String(EXPECTED_COUNT)} entries, got ${String(raw.length)}`,
    )
  }
  for (let i = 0; i < raw.length; i += 1) {
    const entry = raw[i]
    if (!isValidEntry(entry)) {
      throw new Error(
        `practice-puzzles.json entry ${String(i)} failed validation`,
      )
    }
    practiceEntries.push(entry)
  }
  practiceAvailable = true
} catch (err) {
  console.error('Letter Flick: practice corpus failed validation', err)
  practiceAvailable = false
  practiceEntries.length = 0
}

export function isPracticeAvailable(): boolean {
  return practiceAvailable
}

// Returns the number of practice puzzles available — 10 if validation passed,
// 0 otherwise. Callers can use this to size UI affordances; the typical use
// is the prev/next bounds check.
export function getPracticeCount(): number {
  return practiceEntries.length
}

// Returns the puzzle at 1-indexed position `n`. Returns null on out-of-range
// or when the practice corpus failed validation. The translation from wire
// shape to runtime Puzzle mirrors corpus.ts's corpusEntryToPuzzle exactly so
// downstream consumers see identical types.
export function getPracticePuzzle(n: number): Puzzle | null {
  if (!practiceAvailable) return null
  if (!Number.isInteger(n) || n < 1 || n > practiceEntries.length) return null
  const entry = practiceEntries[n - 1]
  if (entry === undefined) return null
  // Parse the optional DAG via the shared validator (re-parsed here, not cached,
  // mirroring corpusEntryToPuzzle — cheap, and keeps the loader stateless). The
  // entry already passed isValidEntry, so a present graph parses to non-undefined;
  // we still treat undefined defensively and omit the key entirely under
  // exactOptionalPropertyTypes rather than set solutionGraph: undefined.
  const solutionGraph: SolutionGraph | undefined = parseWireSolutionGraph(
    entry.solution_graph,
  )
  const base = {
    id: entry.id,
    targetWord: entry.targetWord,
    wordLength: entry.wordLength,
    initialLayout: entry.initialLayout,
    letterByTokenIdx: entry.letterByTokenIdx,
  }
  if (solutionGraph === undefined) return base
  return { ...base, solutionGraph }
}
